import { Fragment, useEffect } from 'react';
import { observer } from 'mobx-react';

import type {
  HostPlayPostMessageConnectionPlayApi,
  HostPlayPostMessageConnectionViewApi,
} from '@genially/contracts';
import { HostPlayP2pClientEventType } from '@genially/p2p-client';
import { PlayUserType } from '@genially/p2p-lib';
import type { PenpalAsyncMethodReturns } from '@genially/ui';
import { SidebarType } from '@genially/ui/build/components/SidebarSummary/types/SidebarType';
import { calculateSidebarTypeFromSelectedQuestion } from '@genially/ui/build/components/SidebarSummary/utils/calculateSidebarTypeFromSelectedQuestion';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';

import { useLogRocketService } from '../../../../../../../../bootstrap/application/components/app/Routes/hooks/useLogRocketService';
import { GeniallyView } from '../../../../../../../../shared/application/components/GeniallyView/GeniallyView';
import { useFullScreen } from '../../../../../../../../shared/application/hooks/useFullScreen';
import { HostPreLobby } from '../../../HostClientProvider/components/HostPreLobby/HostPreLobby';
import { useHostClientContext } from '../../../HostClientProvider/hooks/useHostClientContext';
import { useHostGameContext } from '../../HostGame.context';
import { useHostCursorPositionSender } from './hooks/useHostCursorPositionSender';
import { getInteractiveQuestionsDataFromClient } from './utils/getInteractiveQuestionsDataFromClient';

const NEAR_TO_BOTTOM_THRESHOLD = 100;

interface HostGeniallyViewProps {
  showGeniallyAsFullHeight?: boolean;
}

export const HostGeniallyView = observer(
  ({ showGeniallyAsFullHeight }: HostGeniallyViewProps) => {
    const { client } = useHostClientContext();
    const { trackedEvents } = useLogRocketService();

    const {
      viewConnection,
      sidebarSummary,
      analyticsSidebar,
      interactiveQuestions,
      instructions,
      hostCursorConfig,
    } = useHostGameContext();

    const { toggleFullScreen, isAvailable } = useFullScreen({
      isConnected: viewConnection.isConnected,
      setIsFullScreen: viewConnection.connection?.setIsFullScreen,
    });

    const { geniallyId } = client;

    const sendCursorPositionIfThrottled = useHostCursorPositionSender({
      isEnabled: hostCursorConfig.isEnabled,
      onSendCursorPosition: (xRatio, yRatio, slideAspectRatio) => {
        client.sendHostCursorPosition({ xRatio, yRatio, slideAspectRatio });
      },
    });

    const resolveInteractiveQuestion = (id: string, isLastScored?: boolean) => {
      client.resolveInteractiveQuestion(id, isLastScored);
    };

    const setRandomQuestionAssignment = (elementId: string, bankQuestionId: string) => {
      client.setRandomQuestionAssignment(elementId, bankQuestionId);
    };

    const handleSlideChange = (slideId: string) => {
      client.sendChangeSlide(slideId);
      sidebarSummary.close();
    };

    const handleToggleSidebarSummary = (
      selectedInteractiveQuestionId: string,
      open: boolean,
    ) => {
      const selectedInteractiveQuestion = interactiveQuestions.find(
        i => i.Id === selectedInteractiveQuestionId,
      );

      if (!selectedInteractiveQuestion) {
        return;
      }

      // The genially's own button is the only way back to a question the host already
      // resolved, so in analytics mode it reopens that question's analytics view rather
      // than the individual summary the mode replaced.
      if (client.game.resultsMode === 'analytics') {
        if (open) {
          analyticsSidebar.openQuestion(selectedInteractiveQuestionId);
        } else {
          analyticsSidebar.close();
        }

        return;
      }

      if (!open) {
        sidebarSummary.close();

        return;
      }

      // Disabling the ranking only silences the ranking itself: the host can still open
      // the summary of a feedback question (survey, open answer, categorizable widget).
      if (
        !client.game.isRankingEnabled &&
        calculateSidebarTypeFromSelectedQuestion(selectedInteractiveQuestion) ===
          SidebarType.Ranking
      ) {
        return;
      }

      sidebarSummary.open();

      sidebarSummary.setSelectedQuestion({
        interactiveQuestion: selectedInteractiveQuestion,
        isLastScored: false,
      });
    };

    const handleNotifyMousePosition = ({
      y,
      slide,
    }: {
      x: number;
      y: number;
      viewportWidth: number;
      viewportHeight: number;
      slide: { xRatio: number; yRatio: number; aspectRatio: number };
    }) => {
      instructions.setIsMouseNearTopOfIframe(y < NEAR_TO_BOTTOM_THRESHOLD);

      sendCursorPositionIfThrottled(slide.xRatio, slide.yRatio, slide.aspectRatio);
    };

    const methods: HostPlayPostMessageConnectionPlayApi = {
      onChangeSlide: handleSlideChange,
      resolveInteractiveQuestion,
      setRandomQuestionAssignment,
      toggleSidebarSummary: handleToggleSidebarSummary,
      getInitialData: () => {
        return {
          isFullScreenAvailable: isAvailable,
          currentSlideId: client.game.currentSlide,
          answersPerInteractiveQuestionId: getInteractiveQuestionsDataFromClient(client),
          isTimerSyncEnabled: client.hasFeatureFlag(
            FEATURE_FLAGS.FEAT_LT4_PLAY_TIMER_SYNC,
          ),
        };
      },
      notifyTimerStateChanged: state => {
        client.sendTimerState(state);
      },
      toggleFullScreen,
      getLogRocketTrackedEvents: () => {
        return trackedEvents;
      },
      notifyMousePosition: handleNotifyMousePosition,
    };

    const handleConnectionChange = (
      conn?: PenpalAsyncMethodReturns<HostPlayPostMessageConnectionViewApi>,
    ) => {
      if (conn) {
        viewConnection.setConnection(conn);
      } else {
        viewConnection.setConnection(undefined);
      }
    };

    useEffect(() => {
      const handleInteractiveQuestionAnswerReceivedEvent = () => {
        if (viewConnection.isConnected) {
          viewConnection.connection.setAnswersPerInteractiveQuestionId(
            getInteractiveQuestionsDataFromClient(client),
          );
        }
      };

      client.on(
        HostPlayP2pClientEventType.InteractiveQuestionAnswerReceived,
        handleInteractiveQuestionAnswerReceivedEvent,
      );

      client.on(
        HostPlayP2pClientEventType.InteractiveQuestionResolved,
        handleInteractiveQuestionAnswerReceivedEvent,
      );

      return () => {
        client.off(
          HostPlayP2pClientEventType.InteractiveQuestionAnswerReceived,
          handleInteractiveQuestionAnswerReceivedEvent,
        );

        client.off(
          HostPlayP2pClientEventType.InteractiveQuestionResolved,
          handleInteractiveQuestionAnswerReceivedEvent,
        );
      };
    }, [client, viewConnection.connection, viewConnection.isConnected]);

    useEffect(() => {
      if (!viewConnection.isConnected) {
        return undefined;
      }

      const { connection } = viewConnection;

      const handleTimerStateRequested = ({ itemId }: { itemId: string }) => {
        connection.requestTimerState(itemId);
      };

      client.on(
        HostPlayP2pClientEventType.TimerStateRequested,
        handleTimerStateRequested,
      );

      return () => {
        client.off(
          HostPlayP2pClientEventType.TimerStateRequested,
          handleTimerStateRequested,
        );
      };
    }, [client, viewConnection.connection, viewConnection.isConnected]);

    return (
      <Fragment>
        {/* GeniallyView must stay mounted below so its iframe can load and connect —
            this only covers it visually until then. */}
        {!viewConnection.isConnected && <HostPreLobby />}
        <GeniallyView<
          HostPlayPostMessageConnectionPlayApi,
          HostPlayPostMessageConnectionViewApi
        >
          type={PlayUserType.Host}
          geniallyId={geniallyId}
          methods={methods}
          showGeniallyAsFullHeight={showGeniallyAsFullHeight}
          onConnectionCreated={handleConnectionChange}
          onConnectionDestroyed={handleConnectionChange}
        />
      </Fragment>
    );
  },
);
